import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Search, Filter, Download, CheckCircle, XCircle, 
  Clock, Calendar, Mail, Phone, MapPin, Building2, CreditCard,
  Eye, Check, X, Loader2, User
} from 'lucide-react';
import { adminVerify, listEventRegistrations, fetchAllEventsPages, updateEventRegistrationStatus } from '../../services/api';
const AdminEventRegistrationsPage = () => {
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filters
  const [filters, setFilters] = useState({
    eventId: '',
    status: '',
    paymentStatus: '',
    participantName: '',
    participantEmail: '',
    participantMobile: '',
    organizationName: '',
    startDate: '',
    endDate: ''
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  useEffect(() => {
    const init = async () => {
      try {
        await adminVerify();
        await loadEvents();
        await refresh();
      } catch (e) {
        navigate('/admin/login');
      }
    };
    init();
  }, [navigate]);

  useEffect(() => {
    refresh();
  }, [filters, pagination.page]);

  const loadEvents = async () => {
    try {
      const data = await fetchAllEventsPages();
      setEvents(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load events:', e);
    }
  };

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });
      
      const data = await listEventRegistrations(params);
      setRegistrations(Array.isArray(data.registrations) ? data.registrations : []);
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
        totalPages: data.totalPages || 0
      }));
    } catch (e) {
      setError(e.message || 'Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleStatusUpdate = async (id, newStatus, notes) => {
    try {
      await updateEventRegistrationStatus(id, newStatus, notes);
      setSuccess(`Registration status updated to ${newStatus}`);
      await refresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to update status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric',
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'registered': 'bg-blue-100 text-blue-800',
      'confirmed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800',
      'attended': 'bg-purple-100 text-purple-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentStatusBadge = (status) => {
    const badges = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'completed': 'bg-green-100 text-green-800',
      'failed': 'bg-red-100 text-red-800',
      'cancelled': 'bg-gray-100 text-gray-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/admin')} 
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{'Event Registrations'}</h1>
              <p className="text-xs text-gray-500">Manage and view all event registrations</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border-l-4 border-red-500 text-red-700 flex items-center justify-between shadow-sm">
            <span className="font-medium">{error}</span>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 rounded-xl bg-green-50 border-l-4 border-green-500 text-green-700 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">{success}</span>
            </div>
            <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">{'Filters'}</h3>
              {Object.values(filters).some(f => f) && (
                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                  {Object.values(filters).filter(f => f).length} active
                </span>
              )}
            </div>
            {Object.values(filters).some(f => f) && (
              <button
                onClick={() => {
                  setFilters({
                    eventId: '',
                    status: '',
                    paymentStatus: '',
                    participantName: '',
                    participantEmail: '',
                    participantMobile: '',
                    organizationName: '',
                    startDate: '',
                    endDate: ''
                  });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <X className="h-3.5 w-3.5" />
                {'Clear All'}
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {/* Event Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Event
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.eventId}
                onChange={(e) => handleFilterChange('eventId', e.target.value)}
              >
                <option value="">All Events</option>
                {events.map(event => (
                  <option key={event._id || event.id} value={event._id || event.id}>
                    {event.title}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Registration Status
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="registered">Registered</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
                <option value="attended">Attended</option>
              </select>
            </div>

            {/* Payment Status Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Payment Status
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.paymentStatus}
                onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
              >
                <option value="">All Payment Statuses</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {/* Participant Name */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Participant Name
              </label>
              <div className="relative">
                <User className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.participantName ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search name..."
                  value={filters.participantName}
                  onChange={(e) => handleFilterChange('participantName', e.target.value)}
                />
                {filters.participantName && (
                  <button
                    onClick={() => handleFilterChange('participantName', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Participant Email */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.participantEmail ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search email..."
                  value={filters.participantEmail}
                  onChange={(e) => handleFilterChange('participantEmail', e.target.value)}
                />
                {filters.participantEmail && (
                  <button
                    onClick={() => handleFilterChange('participantEmail', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Participant Mobile */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Mobile
              </label>
              <div className="relative">
                <Phone className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.participantMobile ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search mobile..."
                  value={filters.participantMobile}
                  onChange={(e) => handleFilterChange('participantMobile', e.target.value)}
                />
                {filters.participantMobile && (
                  <button
                    onClick={() => handleFilterChange('participantMobile', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Organization Name */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Organization
              </label>
              <div className="relative">
                <Building2 className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.organizationName ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search organization..."
                  value={filters.organizationName}
                  onChange={(e) => handleFilterChange('organizationName', e.target.value)}
                />
                {filters.organizationName && (
                  <button
                    onClick={() => handleFilterChange('organizationName', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                From Date
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                To Date
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                min={filters.startDate}
              />
            </div>
          </div>

        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600 mb-1">Total Registrations</div>
            <div className="text-2xl font-bold text-gray-900">{pagination.total}</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600 mb-1">Confirmed</div>
            <div className="text-2xl font-bold text-green-600">
              {registrations.filter(r => r.status === 'confirmed').length}
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600 mb-1">Pending Payment</div>
            <div className="text-2xl font-bold text-yellow-600">
              {registrations.filter(r => r.paymentStatus === 'pending').length}
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600 mb-1">Total Revenue</div>
            <div className="text-2xl font-bold text-primary-600">
              ₹{registrations
                .filter(r => r.paymentStatus === 'completed')
                .reduce((sum, r) => sum + (r.registrationFee?.amount || 0), 0)
                .toLocaleString('en-IN')}
            </div>
          </div>
        </div>

        {/* Registrations List */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          </div>
          
          <div className="divide-y divide-gray-200">
            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
                <p className="text-gray-600">Loading registrations...</p>
              </div>
            ) : registrations.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-gray-600 mb-4 font-medium">No registrations found.</p>
                <p className="text-sm text-gray-500">Try adjusting your filters.</p>
              </div>
            ) : (
              registrations.map(reg => (
                <div 
                  key={reg._id} 
                  className="p-6 flex items-start justify-between gap-6 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 transition-all"
                >
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 text-lg">{reg.participantName}</h4>
                        <p className="text-sm text-gray-600 mt-1">{reg.eventTitle || (reg.eventId?.title || 'N/A')}</p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(reg.status)}`}>
                          {reg.status}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPaymentStatusBadge(reg.paymentStatus)}`}>
                          Payment: {reg.paymentStatus}
                        </span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span>{reg.participantEmail}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span>{reg.participantMobile}</span>
                      </div>
                      {reg.organizationName && (
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-gray-400" />
                          <span>{reg.organizationName}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-gray-400" />
                        <span>₹{reg.registrationFee?.amount?.toLocaleString('en-IN') || '0'}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        <span>Registered: {formatDate(reg.registeredAt)}</span>
                      </div>
                      {reg.confirmedAt && (
                        <div className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          <span>Confirmed: {formatDate(reg.confirmedAt)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button 
                      onClick={() => {
                        setSelectedRegistration(reg);
                        setShowDetails(true);
                      }}
                      className="p-3 border-2 border-blue-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all"
                      title="View Details"
                    >
                      <Eye className="h-5 w-5 text-blue-600" />
                    </button>
                    {reg.status !== 'confirmed' && reg.paymentStatus === 'completed' && (
                      <button 
                        onClick={() => handleStatusUpdate(reg._id, 'confirmed', 'Confirmed by admin')}
                        className="p-3 border-2 border-green-200 rounded-lg hover:bg-green-50 hover:border-green-300 transition-all"
                        title="Confirm"
                      >
                        <Check className="h-5 w-5 text-green-600" />
                      </button>
                    )}
                    {reg.status !== 'cancelled' && (
                      <button 
                        onClick={() => {
                          if (window.confirm('Are you sure you want to cancel this registration?')) {
                            handleStatusUpdate(reg._id, 'cancelled', 'Cancelled by admin');
                          }
                        }}
                        className="p-3 border-2 border-red-200 rounded-lg hover:bg-red-50 hover:border-red-300 transition-all"
                        title="Cancel"
                      >
                        <X className="h-5 w-5 text-red-600" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-8 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Registration Details Modal */}
      {showDetails && selectedRegistration && (
        <div className="fixed inset-0 z-[1000] flex items-stretch sm:items-center justify-end bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-white shadow-2xl w-full sm:w-[92vw] md:w-[82vw] lg:w-[70vw] xl:w-[60vw] 2xl:w-[52vw] max-w-[960px] h-full sm:h-[calc(100vh-32px)] sm:my-4 overflow-hidden flex flex-col rounded-none sm:rounded-2xl overflow-x-hidden min-w-0">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Registration Details</h3>
              <button
                onClick={() => {
                  setShowDetails(false);
                  setSelectedRegistration(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            
            <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-6">
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Event Information</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                    <div><span className="font-medium">Event:</span> {selectedRegistration.eventTitle || 'N/A'}</div>
                    <div><span className="font-medium">Registration Date:</span> {formatDate(selectedRegistration.registeredAt)}</div>
                    <div><span className="font-medium">Status:</span> 
                      <span className={`ml-2 px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(selectedRegistration.status)}`}>
                        {selectedRegistration.status}
                      </span>
                    </div>
                    <div><span className="font-medium">Payment Status:</span> 
                      <span className={`ml-2 px-2 py-1 rounded text-xs font-semibold ${getPaymentStatusBadge(selectedRegistration.paymentStatus)}`}>
                        {selectedRegistration.paymentStatus}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Participant Details</h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
                    <div><span className="font-medium text-gray-600">Name:</span> {selectedRegistration.participantName}</div>
                    <div><span className="font-medium text-gray-600">Email:</span> {selectedRegistration.participantEmail}</div>
                    <div><span className="font-medium text-gray-600">Mobile:</span> {selectedRegistration.participantMobile}</div>
                    {selectedRegistration.organizationName && (
                      <div><span className="font-medium text-gray-600">Organization:</span> {selectedRegistration.organizationName}</div>
                    )}
                    {selectedRegistration.designation && (
                      <div><span className="font-medium text-gray-600">Designation:</span> {selectedRegistration.designation}</div>
                    )}
                  </div>
                </div>

                {(selectedRegistration.address || selectedRegistration.city || selectedRegistration.state || selectedRegistration.pincode) && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Address</h4>
                    <div className="text-sm text-gray-700">
                      {selectedRegistration.address && <div>{selectedRegistration.address}</div>}
                      <div>
                        {[selectedRegistration.city, selectedRegistration.state, selectedRegistration.pincode]
                          .filter(Boolean)
                          .join(', ')}
                      </div>
                    </div>
                  </div>
                )}

                {(selectedRegistration.specialRequirements || selectedRegistration.dietaryPreferences) && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Additional Information</h4>
                    <div className="text-sm text-gray-700 space-y-2">
                      {selectedRegistration.specialRequirements && (
                        <div><span className="font-medium">Special Requirements:</span> {selectedRegistration.specialRequirements}</div>
                      )}
                      {selectedRegistration.dietaryPreferences && (
                        <div><span className="font-medium">Dietary Preferences:</span> {selectedRegistration.dietaryPreferences}</div>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Payment Information</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                    <div><span className="font-medium">Registration Fee:</span> ₹{selectedRegistration.registrationFee?.amount?.toLocaleString('en-IN') || '0'}</div>
                    {selectedRegistration.orderId && (
                      <div><span className="font-medium">Order ID:</span> {selectedRegistration.orderId}</div>
                    )}
                    {selectedRegistration.paymentId && (
                      <div><span className="font-medium">Payment ID:</span> {selectedRegistration.paymentId._id || selectedRegistration.paymentId}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:justify-end gap-3">
              <button
                onClick={() => {
                  setShowDetails(false);
                  setSelectedRegistration(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors w-full sm:w-auto"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEventRegistrationsPage;
